<!-- eslint-disable tailwindcss/migration-from-tailwind-2 -->
<template>
  <div class="bg-white rounded-xl">
    <div class="relative w-full">
      <form @submit.prevent="handleKeydown()">
        <div
          class="relative z-20 flex items-center border rounded-t-sm h-14 bg-extra-light-linen"
          :class="{
            'border-sienna-muted rounded-b-sm': !isFocused,
            'border-terracota-muted': isFocused,
            [innerStyle ?? '']: true,
          }"
        >
          <LocationMarkerIcon
            class="ml-3 size-6 text-sienna-muted"
            :class="{ 'hidden md:block': shouldShowGeolocationBtn }"
          />
          <GeoLocationButton
            v-if="shouldShowGeolocationBtn"
            class="border-none outline-none md:hidden focus:bg-none"
            :is-focused="isFocused"
            inside-input-style
            @on-position="handleGeoLocationSearch"
          />
          <label for="deliver-address" class="flex-1">
            <span class="sr-only">Enter your delivery address</span>
            <input
              id="deliver-address"
              ref="addressInput"
              :value="text"
              class="flex-1 w-full font-light text-[#806E61] placeholder-sienna-muted border-none outline-none focus-visible:ring-0 bg-transparent"
              placeholder="Enter postcode"
              type="text"
              autocomplete="off"
              spellcheck="false"
              aria-autocomplete="both"
              @input="handleInputUpdate"
              @change="handleInputUpdate"
              @focus="handleFocus"
              @keydown.enter.prevent="NOOP"
            />
          </label>
          <button aria-label="clear" @click="clearText">
            <XIcon v-if="text" class="size-5 mr-3 text-[#806E61]" />
          </button>
        </div>
        <button id="deliver-address-btn" type="submit" hidden="true"></button>
        <ul
          id="address-suggestions-box"
          :aria-expanded="isExpanded"
          class="absolute inset-x-0 overflow-x-hidden text-sm bg-extra-light-linen z-20 max-h-64 overflow-y-auto list-inside list-none scrollbar-thin scrollbar-thumb-[#806E61] scrollbar-track-sienna-muted border border-t-0"
          :class="{
            'border-sienna-muted': !isFocused,
            'border-terracota-muted': isFocused,
            'border-0': shouldShowBottomBorder,
          }"
        >
          <GeoLocationPinnedBtn
            v-if="((!text && isExpanded) || isFetchingUserGpsLocation) && isMobile"
            @on-position="handleGeoLocationSearch"
          />
          <PreviousSavedSuggestions
            v-if="shouldShowPreviousSuggestions && isExpanded"
            @saved-address-press="handleSavedSuggestionPress"
          />

          <li
            v-for="(suggestion, index) in suggestionList"
            :key="suggestion.Id"
            class="border-b border-sandstone hover:bg-light-linen hover:border-0 bg-extra-light-linen"
          >
            <button
              :ref="skipUnwrap.el"
              class="relative flex items-center justify-between w-full h-16 px-4 py-3 cursor-pointer"
              role="option"
              aria-selected="false"
              :data-index="index"
              :data-type="suggestion.Type"
              :aria-labelledby="`suggestion-description-${index}`"
              @click.prevent="handleSuggestionPress(suggestion)"
              @blur="handleButtonBlur"
            >
              <div class="flex items-center">
                <div>
                  <LocationMarkerIcon class="mr-2 size-5 text-dark-linen" />
                </div>
                <div class="flex flex-col items-start">
                  <BaseAddressSuggestion
                    :suggestion="suggestion"
                    highlight-class-style="font-semibold"
                    class="text-sm text-left text-off-black"
                  />
                  <p :id="`suggestion-description-${index}`" class="text-xs text-[#806E61]">
                    {{ suggestion.Description }}
                  </p>
                </div>
              </div>
              <ChevronRightIcon
                v-if="suggestion.Type === 'Container'"
                class="absolute flex size-5 text-burnt-sienna right-4 sm:bg-transparent"
              />
            </button>
          </li>
        </ul>
      </form>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import useAddressFinder, { FindItem } from '@/composables/useAddressFinder';
  import { computed, ref } from 'vue';
  import BaseAddressSuggestion from '@/components/BaseAddressSuggestion.vue';
  import { ManualAddressInputPayload } from '@/components/BaseAddressInputs.vue';
  import useSafariBlur from '@/composables/useSafariBlur';
  import { ChevronRightIcon, LocationMarkerIcon, XIcon } from '@heroicons/vue/solid';
  import useUpAndDownArrowNavigation from '@/composables/useUpAndDownArrowNavigation';
  import PreviousSavedSuggestions from './PreviousSavedSuggestions.vue';
  import usePreviousSuggestions, {
    ManualAddressInputWithDescription,
  } from '@/composables/usePreviousSuggestions';
  import { trackCustomEvent } from '@/utils/analytics';
  import useReverGeoLocation from '@/composables/useReverseGeoLocation';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import { useMutationState } from '@tanstack/vue-query';
  import { NOOP } from '@/utils';

  interface Props {
    innerStyle?: string;
  }
  defineProps<Props>();

  interface Emits {
    (e: 'suggestionPress', value: ManualAddressInputPayload): void;
    (e: 'press', value: { id: string } | ManualAddressInputPayload): void;
    (e: 'focus', value: FocusEvent): void;
  }

  const emit = defineEmits<Emits>();

  const addressInput = ref<HTMLInputElement>();
  const skipUnwrap = { el: ref<HTMLElement[]>([]) };
  const isFocused = ref(false);
  const blockSearch = ref(false);

  const { text, suggestionList, retrieve, find } = useAddressFinder({ blockSearch });
  const { saveAddress, isEmpty, savedSuggestions } = usePreviousSuggestions();
  const { getAddressFromLocation } = useReverGeoLocation();
  const breakpoints = useBreakpoints(breakpointsTailwind);

  defineExpose({ addressInput });

  const suggestionListElements = computed(() => {
    const suggestionElements = skipUnwrap.el.value as HTMLDivElement[];
    return suggestionElements.sort((a, b) => {
      if (a.getAttribute('data-index') && b.getAttribute('data-index'))
        return +a.getAttribute('data-index')! - +b.getAttribute('data-index')!;
      else if (a.getAttribute('data-index')) return 1;
      return -1;
    });
  });

  const userGpsLocationMutationStatus = useMutationState({
    filters: { mutationKey: ['userGpsLocation'] },
    select: mutation => mutation.state.status,
  });

  const isFetchingUserGpsLocation = computed(() => {
    if (!userGpsLocationMutationStatus.value.length) return false;
    const mutationStatusLength = userGpsLocationMutationStatus.value.length;
    return userGpsLocationMutationStatus.value[mutationStatusLength - 1] === 'pending';
  });

  const shouldShowPreviousSuggestions = computed(() => {
    return !suggestionList.value?.length && !isEmpty.value;
  });

  const isMobile = breakpoints.isSmaller('lg');

  const shouldShowGeolocationBtn = computed(() => {
    return isMobile;
  });

  const { handleButtonBlur } = useUpAndDownArrowNavigation({
    input: addressInput,
    list: suggestionListElements,
  });

  const handleBlur = () => {
    suggestionList.value = [];
    isFocused.value = false;
  };

  const handleFocus = (e: FocusEvent) => {
    emit('focus', e);
    isFocused.value = true;
  };

  useSafariBlur(handleBlur, e => {
    return (
      e.id === 'deliver-address-btn' ||
      e.id === 'deliver-address' ||
      e.dataset.type === 'Container' ||
      e.id === 'address-lookup-change-address-span' ||
      e.innerHTML === 'Change'
    );
  });

  const isExpanded = computed(() => !!suggestionList.value?.length || isFocused.value);

  const shouldShowBottomBorder = computed(() => {
    const isNotFocusedAndNoSuggestions =
      !isFocused.value && !isFetchingUserGpsLocation.value && !suggestionList.value?.length;

    const isDesktopHasNoSuggestionsAndNoSavedSuggestions =
      isFocused.value &&
      !isMobile &&
      !suggestionList.value?.length &&
      (!savedSuggestions.value || Object.keys(savedSuggestions.value).length === 0);

    return isNotFocusedAndNoSuggestions || isDesktopHasNoSuggestionsAndNoSavedSuggestions;
  });

  const handleSuggestionPress = async (suggestion: FindItem) => {
    if (suggestion.Type === 'Address') {
      handleSuggestionSubmission(suggestion.Id);
      return;
    }
    const results = await find({ text: text.value, containerId: suggestion.Id });

    if (!results) return;

    suggestionList.value = results.Items;
  };

  interface SavedSuggestionPressPayload {
    suggestion: ManualAddressInputWithDescription;
    id: string;
  }

  const handleSavedSuggestionPress = (payload: SavedSuggestionPressPayload) => {
    emit('suggestionPress', payload.suggestion);
    saveAddress({ id: payload.id, address: { ...payload.suggestion } });
    emit('press', { id: payload.id });

    blockSearch.value = true;
    isFocused.value = false;
    text.value = payload.suggestion.description;
    isFocused.value = false;
    setTimeout(() => {
      blockSearch.value = false;
    }, 350);
  };

  const handleSuggestionSubmission = async (id: string) => {
    blockSearch.value = true;
    const { Items } = await retrieve(id);
    const company = Items[0].Company;

    const suggestedAddress = {
      address1: (company ? `${company}, ` : '') + Items[0].Line1,
      address2: Items[0].Line2 + (Items[0].Line3 ? ', ' + Items[0].Line3 : ''),
      city: Items[0].City,
      zip: Items[0].PostalCode,
      region: Items[0].ProvinceName,
      regionCode: Items[0].ProvinceCode,
      company: Items[0].Company,
      country: Items[0].CountryName,
      countryCode: Items[0].CountryIso2,
    };

    emit('suggestionPress', suggestedAddress);

    saveAddress({
      id: Items[0].Id,
      address: { ...suggestedAddress, description: Items[0].Label.replaceAll('\n', ', ') },
    });

    emit('press', suggestedAddress);

    isFocused.value = false;
    text.value = Items[0].Label.replaceAll('\n', ', ');
    isFocused.value = false;
    setTimeout(() => {
      blockSearch.value = false;
    }, 350);
  };

  const handleKeydown = () => {
    if (text.value && suggestionList.value?.[0]) {
      handleSuggestionPress(suggestionList.value[0]);
    }
  };

  const handleInputUpdate = (e: Event) => {
    text.value = (e.target as HTMLInputElement).value;
  };

  const clearText = () => {
    text.value = '';
    addressInput.value?.focus();
  };

  const handleGeoLocationSearch = async (position: GeolocationPosition['coords']) => {
    trackCustomEvent('geolocation-search');
    const result = await getAddressFromLocation({
      latitude: position.latitude,
      longitude: position.longitude,
    });

    if (result) {
      text.value = result.Postcode;
    }
  };

  nextTick(() => {
    addressInput.value?.focus();
  });
</script>
